import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Dumbbell, Target, Loader2 } from "lucide-react";
import { EXERCISES } from "@/data/exercises";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CustomExercise {
  name: string;
  category: string;
  muscle_groups: string[];
  equipment?: string;
  description?: string;
  instructions?: string;
}

interface ExerciseInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

export const ExerciseInfoModal = ({ isOpen, onClose, exerciseName }: ExerciseInfoModalProps) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [customExercise, setCustomExercise] = useState<CustomExercise | null>(null);
  
  // Find the exercise from the built-in database
  const builtInExercise = EXERCISES.find(ex => 
    ex.name.toLowerCase() === exerciseName.toLowerCase()
  );

  // Helper to normalize names for matching
  const normalizeName = (str: string) =>
    str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const levenshteinDistance = (a: string, b: string) => {
    const matrix: number[][] = [];
    const lenA = a.length;
    const lenB = b.length;

    for (let i = 0; i <= lenB; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= lenA; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= lenB; i++) {
      for (let j = 1; j <= lenA; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[lenB][lenA];
  };

  // Load exercise image from Supabase Storage
  useEffect(() => {
    const loadExerciseImage = async () => {
      if (!exerciseName || !isOpen) return;

      setIsLoadingImage(true);
      setImageUrl("");

      const rawNormalized = exerciseName.toLowerCase().replace(/\s+/g, "-");
      const normalized = normalizeName(exerciseName);
      const possibleExtensions = ["png", "jpg", "jpeg", "webp"];

      // Create variations of the filename to try directly
      const fileVariations = [
        rawNormalized, // original whitespace->hyphen version e.g., "hack-squat-machine"
        rawNormalized.split("-")[0], // first word only
        rawNormalized.split("-").slice(0, 2).join("-"), // first two words
        exerciseName.toLowerCase().replace(/\s+/g, ""), // no separators
      ].filter(Boolean);

      let imageFound = false;

      // First: try direct filename guesses
      for (const variation of fileVariations) {
        if (imageFound) break;

        for (const ext of possibleExtensions) {
          const filename = `${variation}.${ext}`;

          const { data } = supabase.storage
            .from("exercise-images")
            .getPublicUrl(filename);

          if (data?.publicUrl) {
            await new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => {
                setImageUrl(data.publicUrl);
                setIsLoadingImage(false);
                imageFound = true;
                resolve();
              };
              img.onerror = () => {
                resolve();
              };
              img.src = data.publicUrl;
            });

            if (imageFound) break;
          }
        }
      }

      // Second: fuzzy match against all stored filenames if still not found
      if (!imageFound) {
        try {
          const { data: files, error } = await supabase.storage
            .from("exercise-images")
            .list();

          if (!error && files && files.length > 0) {
            const candidates = files.map((f) => ({
              name: f.name,
              base: f.name.replace(/\.(png|jpg|jpeg|webp)$/i, ""),
            }));

            let bestMatch: { name: string; score: number } | null = null;

            for (const c of candidates) {
              const normImg = normalizeName(c.base);
              let score = levenshteinDistance(normalized, normImg);

              // Small bonus if the first word matches
              const firstWord = normalized.split("-")[0];
              if (normImg.includes(firstWord)) {
                score -= 1;
              }

              if (!bestMatch || score < bestMatch.score) {
                bestMatch = { name: c.name, score };
              }
            }

            if (bestMatch && bestMatch.score <= 3) {
              const { data } = supabase.storage
                .from("exercise-images")
                .getPublicUrl(bestMatch.name);

              if (data?.publicUrl) {
                await new Promise<void>((resolve) => {
                  const img = new Image();
                  img.onload = () => {
                    setImageUrl(data.publicUrl);
                    resolve();
                  };
                  img.onerror = () => {
                    resolve();
                  };
                  img.src = data.publicUrl;
                });
              }
            }
          }
        } catch (e) {
          console.error("Error fuzzy-matching exercise image", e);
        }

        setIsLoadingImage(false);
      }
    };

    loadExerciseImage();
  }, [exerciseName, isOpen]);

  // Load custom exercise if not found in built-in exercises
  useEffect(() => {
    const loadCustomExercise = async () => {
      if (!builtInExercise && isOpen && exerciseName) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data, error } = await supabase
            .from('custom_exercises')
            .select('*')
            .eq('user_id', user.id)
            .ilike('name', exerciseName)
            .maybeSingle();

          if (error) throw error;
          if (data) {
            setCustomExercise(data);
          }
        } catch (error) {
          console.error('Error loading custom exercise:', error);
        }
      }
    };

    loadCustomExercise();
  }, [isOpen, exerciseName, builtInExercise]);


  // Combine built-in and custom exercise data
  const exercise = builtInExercise || (customExercise ? {
    ...customExercise,
    id: 'custom',
  } : null);

  // Generate basic instructions based on category and exercise type
  const generateInstructions = () => {
    if (!exercise) {
      return t('components.exerciseInfo.noInfo');
    }

    const name = exercise.name.toLowerCase();
    const category = exercise.category.toLowerCase();

    if (category === 'cardio' || name.includes('running') || name.includes('cycling')) {
      return t('components.exerciseInfo.instructions.cardio');
    }

    if (name.includes('bench press')) {
      return t('components.exerciseInfo.instructions.benchPress');
    }

    if (name.includes('squat')) {
      return t('components.exerciseInfo.instructions.squat');
    }

    if (name.includes('deadlift')) {
      return t('components.exerciseInfo.instructions.deadlift');
    }

    if (name.includes('pull-up') || name.includes('chin-up')) {
      return t('components.exerciseInfo.instructions.pullUp');
    }

    if (name.includes('row')) {
      return t('components.exerciseInfo.instructions.row');
    }

    if (name.includes('press') && category.includes('shoulder')) {
      return t('components.exerciseInfo.instructions.shoulderPress');
    }

    if (name.includes('curl')) {
      return t('components.exerciseInfo.instructions.curl');
    }

    if (name.includes('lunge')) {
      return t('components.exerciseInfo.instructions.lunge');
    }

    return t('components.exerciseInfo.instructions.default');
  };

  if (!exercise) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Dumbbell className="h-6 w-6 text-primary" />
              {exerciseName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Exercise Demonstration Image */}
            <div 
              className="flex items-center justify-center bg-muted rounded-lg overflow-hidden relative"
              style={{ 
                aspectRatio: '1/1', 
                maxHeight: '500px',
                boxShadow: imageUrl ? '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3), inset 0 0 60px rgba(16, 185, 129, 0.08)' : 'none',
                border: imageUrl ? '1px solid rgba(16, 185, 129, 0.4)' : 'none'
              }}
            >
              {isLoadingImage ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="text-sm text-muted-foreground">{t('components.exerciseInfo.loadingImage')}</p>
                </div>
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt={t('components.exerciseInfo.demonstrationAlt', { name: exerciseName })}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 p-4 text-center">
                  <Dumbbell className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t('components.exerciseInfo.noImage')}</p>
                </div>
              )}
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {t('components.exerciseInfo.noDetail')}
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
              <h3 className="text-sm font-semibold text-primary mb-2">{t('components.exerciseInfo.tipsTitle')}</h3>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• {t('components.exerciseInfo.tipWarmUp')}</li>
                <li>• {t('components.exerciseInfo.tipForm')}</li>
                <li>• {t('components.exerciseInfo.tipBreathe')}</li>
                <li>• {t('components.exerciseInfo.tipPain')}</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Dumbbell className="h-6 w-6 text-primary" />
            {exercise.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Exercise Demonstration Image */}
          <div 
            className="flex items-center justify-center bg-muted rounded-lg overflow-hidden relative"
            style={{ 
              aspectRatio: '1/1', 
              maxHeight: '500px',
              boxShadow: imageUrl ? '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3), inset 0 0 60px rgba(16, 185, 129, 0.08)' : 'none',
              border: imageUrl ? '1px solid rgba(16, 185, 129, 0.4)' : 'none'
            }}
          >
            {isLoadingImage ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">{t('components.exerciseInfo.loadingImage')}</p>
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={t('components.exerciseInfo.demonstrationAlt', { name: exerciseName })}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 p-4 text-center">
                <Dumbbell className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('components.exerciseInfo.noImage')}</p>
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('components.exerciseInfo.category')}</h3>
            <Badge variant="secondary" className="text-sm">
              {exercise.category}
            </Badge>
          </div>

          {/* Equipment */}
          {exercise.equipment && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('components.exerciseInfo.equipmentNeeded')}</h3>
              <Badge variant="outline" className="text-sm">
                {exercise.equipment}
              </Badge>
            </div>
          )}

          {/* Muscle Groups */}
          {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" />
                {t('components.exerciseInfo.muscleGroups')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {exercise.muscle_groups.map((group, index) => (
                  <Badge 
                    key={index} 
                    className="text-sm px-3 py-1 bg-primary/10 text-primary border-primary/20"
                  >
                    {group}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">{t('components.exerciseInfo.howToPerform')}</h3>
            <div className="bg-background/50 rounded-lg p-4 border border-border/50">
              <div className="space-y-2 text-sm leading-relaxed whitespace-pre-line">
                {generateInstructions()}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
            <h3 className="text-sm font-semibold text-primary mb-2">{t('components.exerciseInfo.proTipsTitle')}</h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• {t('components.exerciseInfo.proTipWarmUp')}</li>
              <li>• {t('components.exerciseInfo.tipForm')}</li>
              <li>• {t('components.exerciseInfo.tipBreathe')}</li>
              <li>• {t('components.exerciseInfo.tipPain')}</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
